/**
 * Contrato e chamadas de agenda, gravação e ata (spec §2.5, F8) — as telas 6 e 7.
 *
 * Cinco regras moram aqui, e nenhuma é detalhe de serialização:
 *
 * · **A fronteira é do servidor; esta camada só a REPETE em voz alta.** O que o cliente vê é
 *   recortado em `app/dominios/reuniao/visibilidade.py` e, antes dele, pela policy
 *   `ata_item_isolamento` da migration `0013`: uma nota interna **não existe** para uma sessão de
 *   cliente, em consulta nenhuma. `oClienteVe` abaixo não filtra nada por segurança — ela existe
 *   para a tela INTERNA dizer, enquanto alguém escreve, para onde aquele item vai. Prévia que se
 *   confunde com tranca é pior que prévia nenhuma, e por isso ela é chamada de prévia na tela.
 *
 * · **O vocabulário que a tela só ROTULA chega como texto; o que ela usa para DECIDIR chega como
 *   enum fechado.** Tipo de reunião, modo, estado de mídia — texto, com rótulo de reserva, pelo
 *   motivo de `rotuloDoEvento` em `console.ts`: um valor novo no banco aparece sem nome bonito em
 *   vez de derrubar a tela inteira. Já `tipo` do item, `responsavel_lado` e `estado` da ata são
 *   `z.enum`: um valor desconhecido neles é um item que a tela não sabe classificar, e a leitura
 *   falhar é melhor do que ele ser desenhado sem lado. Fecha para o lado seguro.
 *
 * · **Só `manual` funciona hoje.** A medição de 25/08 não achou uma linha de uso programático do
 *   Google Calendar na organização, nem service account com delegação de domínio. O banco recusa
 *   uma linha `calendar` sem `evento_externo_id` — quer dizer: enquanto o adaptador não existir,
 *   `calendar` é impossível de gravar. `MODOS_DA_REUNIAO` declara os dois e diz qual está de pé,
 *   para a tela mostrar a dívida onde ela é procurada em vez de escondê-la.
 *
 * · **Jira desligado é pendência, nunca erro** — a mesma regra do pacote (F9).
 *   `PORTAL_JIRA_TOKEN` e `PORTAL_JIRA_EMAIL` estão vazios no servidor de propósito, e o portal
 *   **não cria** issue de pendência (ADR 0002: quem cria no ONB é a Automation). O que funciona é
 *   vincular a chave que já existe. A rota responde `200` com o motivo escrito em
 *   `jira_pendencia`, e é assim que a tela o chama.
 *
 * · **Enviar transcrição pronta é caminho de primeira classe, não remendo.** Sem credencial do
 *   `geo-ai-gateway` e sem `ffmpeg` no container, é por ele — ou pela ata escrita à mão — que uma
 *   reunião de 1h30 entra no portal. `estadoDoPipeline` vem junto da listagem de mídia
 *   justamente para a tela não desenhar barra de progresso que nunca anda.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  BASE_DA_API,
  ErroDaApi,
  apagar,
  editarJson,
  enviarFormulario,
  enviarJson,
  pedirJson,
} from "./api";
import { CHAVE_LISTAGEM, CHAVE_MINHA_ESTEIRA } from "./implantacoes";
import { CHAVE_RAIZ_DO_CONSOLE } from "./console";

/* -------------------------------------------------------------------------- permissões */

/* Espelham a matriz da spec §4 e servem para a tela **não oferecer** o que o servidor vai negar —
   nunca para proteger. Quem nega é o serviço, que tem a sessão de banco e a auditoria. */
export const PERMISSAO_DE_AGENDAR = "reuniao:agendar";
export const PERMISSAO_DE_VER_ATA = "ata:ver";
export const PERMISSAO_DE_EDITAR_ATA = "ata:editar";
export const PERMISSAO_DE_PUBLICAR_ATA = "ata:publicar";
/* A que abre a nota interna e o material bruto. A gravação publicada ao cliente vem no resumo da
   reunião e não depende desta permissão; áudio e transcrição continuam internos. */
export const PERMISSAO_DE_VER_NOTA_INTERNA = "ata:ver_interno";

/* --------------------------------------------------------------------------- vocabulário */

export const TIPOS_DE_REUNIAO = [
  "kickoff",
  "acompanhamento",
  "treinamento",
  "validacao_integracao",
  "encerramento",
] as const;

export type TipoDeReuniao = (typeof TIPOS_DE_REUNIAO)[number];

export const ROTULO_DO_TIPO_DE_REUNIAO: Record<string, string> = {
  kickoff: "Kickoff",
  acompanhamento: "Acompanhamento",
  treinamento: "Treinamento",
  validacao_integracao: "Validação da integração",
  encerramento: "Encerramento",
};

export const ROTULO_DO_ESTADO_DA_REUNIAO: Record<string, string> = {
  agendada: "Agendada",
  realizada: "Realizada",
  cancelada: "Cancelada",
};

export const ROTULO_DO_TIPO_DE_MIDIA: Record<string, string> = {
  gravacao: "Gravação",
  audio: "Áudio",
  transcricao: "Transcrição",
};

export const ROTULO_DO_ESTADO_DA_MIDIA: Record<string, string> = {
  pendente: "Na fila",
  processando: "Processando",
  concluida: "Pronta",
  falhou: "Falhou",
};

export const ROTULO_DA_ETAPA_DO_PIPELINE: Record<string, string> = {
  extracao_de_audio: "Extração do áudio (ffmpeg)",
  transcricao: "Transcrição",
  resumo: "Resumo pela IA",
};

/** Os três tipos de item da ata. Fechado: é ele que decide o lado da mesa. */
export const TIPOS_DE_ITEM = ["decisao", "pendencia", "nota_interna"] as const;
export type TipoDeItem = (typeof TIPOS_DE_ITEM)[number];

export const ROTULO_DO_TIPO_DE_ITEM: Record<TipoDeItem, string> = {
  decisao: "Decisão",
  pendencia: "Pendência",
  nota_interna: "Nota interna",
};

export const LADOS_DO_RESPONSAVEL = ["interno", "cliente"] as const;
export type LadoDoResponsavel = (typeof LADOS_DO_RESPONSAVEL)[number];

export const ROTULO_DO_LADO: Record<LadoDoResponsavel, string> = {
  interno: "GEOvendas",
  cliente: "Cliente",
};

/**
 * Os dois modos da spec — e qual deles existe.
 *
 * `calendar` fica declarado e desligado de propósito: escondê-lo esconderia a dívida do único
 * lugar onde ela é procurada, que é a tela de marcar reunião. O `motivo` é o que a tela mostra
 * ao lado da opção que não pode ser escolhida.
 */
export const MODOS_DA_REUNIAO = [
  {
    valor: "manual",
    rotulo: "Manual — você marca no seu calendário e registra aqui",
    disponivel: true,
    motivo: null,
  },
  {
    valor: "calendar",
    rotulo: "Google Calendar / Meet — o portal cria o evento",
    disponivel: false,
    /* A medição de 25/08 não encontrou uso programático do Google Calendar na organização, nem
       service account com delegação de domínio; o adaptador está declarado e sem implementação,
       e o banco recusa uma reunião `calendar` sem o id do evento externo. Isso é o porquê, e
       fica aqui; na tela vai só o que a pessoa precisa saber (spec 4.4). */
    motivo: "Ainda não disponível. Marque no seu calendário e registre aqui.",
  },
] as const;

/** Duração máxima aceita pelo servidor (`DURACAO_MAXIMA_EM_MINUTOS`), repetida para o campo. */
export const DURACAO_MAXIMA_EM_MINUTOS = 600;
/** O padrão de uma reunião de kickoff — 1h30, que é a do gate da fatia. */
export const DURACAO_PADRAO_EM_MINUTOS = 90;
export const MAXIMO_DO_RESUMO = 50_000;
export const MAXIMO_DO_TEXTO_DO_ITEM = 5_000;
export const MAXIMO_DO_RESPONSAVEL = 200;
/** 24 h em minutos: o teto do carimbo de minuto de um item (`MINUTO_MAXIMO_DO_ITEM`). */
export const MINUTO_MAXIMO_DO_ITEM = 1440;
export const MOTIVO_MINIMO_DE_CANCELAMENTO = 3;
export const MOTIVO_MAXIMO_DE_CANCELAMENTO = 500;

/* Extensões aceitas por tipo, iguais às de `pipeline.FORMATOS_POR_TIPO`. Repetidas aqui para o
   seletor de arquivo filtrar ANTES do envio: quem escolhe um `.mp4` de 1h30 e só descobre no
   `409` esperou o upload inteiro para ler a recusa. Quem recusa continua sendo o servidor. */
export const EXTENSOES_DE_GRAVACAO = [".mp4", ".mkv", ".mov", ".webm", ".avi"] as const;
export const EXTENSOES_DE_AUDIO = [".mp3", ".m4a", ".wav", ".ogg", ".opus", ".aac", ".flac"] as const;
export const EXTENSOES_DE_TRANSCRICAO = [".txt", ".vtt", ".srt", ".md", ".json"] as const;

/** Teto do upload, igual ao `client_max_body_size` do nginx. Ver o item 6 de `modelos.py`. */
export const TAMANHO_MAXIMO_DA_MIDIA_EM_BYTES = 100 * 1024 * 1024;

/* ----------------------------------------------------------------------------- esquemas */

const esquemaPessoa = z.object({
  id: z.string().min(1),
  nome: z.string().min(1),
  email: z.string().min(1),
});

/* Texto, e não `z.enum`: o vocabulário é fechado no BANCO (tipo enumerado) e um valor novo entra
   por `ALTER TYPE`. Se esta camada repetisse a lista, o primeiro valor novo derrubaria a tela em
   vez de aparecer sem rótulo bonito — que é o pior jeito de descobrir que houve uma migration. */
const esquemaRotulado = z.string().min(1);

const esquemaGravacaoVisivel = z.object({
  id: z.string().min(1),
  nome: z.string().min(1),
  tamanho: z.number().nonnegative(),
  duracao_seg: z.number().positive().nullable(),
  arquivo_url: z.string().startsWith("/api/"),
  criada_em: z.string().min(1),
});

export const esquemaReuniao = z.object({
  id: z.string().min(1),
  implantacao_id: z.string().min(1),
  assunto: z.string().min(1),
  tipo: esquemaRotulado,
  modulo_chave: z.string().nullable().optional().default(null),
  modo: esquemaRotulado,
  estado: esquemaRotulado,
  agendada_para: z.string().min(1),
  duracao_min: z.number(),
  link: z.string().nullable(),
  visivel_ao_cliente: z.boolean(),
  /* Sempre nulo hoje, e presente de propósito: a tela precisa saber a verdade para não desenhar
     um botão de calendário que não leva a lugar nenhum. */
  evento_externo_id: z.string().nullable(),
  realizada_em: z.string().nullable(),
  cancelada_em: z.string().nullable(),
  agendada_por: esquemaPessoa.nullable(),
  criada_em: z.string().min(1),
  /* A ata existe **para quem está pedindo**: para o cliente, rascunho é `false`. Um "tem ata, mas
     você não pode ler" contaria que alguém escreveu algo sobre a reunião dele. */
  tem_ata: z.boolean(),
  ata_publicada: z.boolean(),
  gravacoes: z.array(esquemaGravacaoVisivel).default([]),
});

/**
 * Um item da ata **já recortado pelo servidor**.
 *
 * `tipo` e `responsavel_lado` são enum fechado aqui, ao contrário do resto: são os dois campos
 * que esta camada usa para dizer para onde o item vai. Valor desconhecido neles derruba a
 * leitura em vez de virar um item desenhado sem lado — é o lado seguro do erro.
 */
export const esquemaItemDaAta = z.object({
  id: z.string().min(1),
  tipo: z.enum(TIPOS_DE_ITEM),
  texto: z.string().min(1),
  responsavel: z.string().nullable(),
  responsavel_lado: z.enum(LADOS_DO_RESPONSAVEL).nullable(),
  prazo: z.string().nullable(),
  minuto: z.number().nullable(),
  visivel_ao_cliente: z.boolean(),
  jira_key: z.string().nullable(),
  jira_enviado_em: z.string().nullable(),
  /* A coluna se chama `jira_ultimo_erro`; o contrato a chama de pendência, porque é o que ela é
     neste servidor. Ver o item 4 do cabeçalho deste módulo. */
  jira_pendencia: z.string().nullable(),
  /* Posição NESTA lista, não a coluna do banco: para o cliente ela é densa de propósito — uma
     numeração com buracos diria quantos itens ficaram de fora e entre quais decisões. */
  ordem: z.number(),
  /* Nula é o estado normal de uma pendência aberta; sempre nula em decisão e nota interna. */
  concluida_em: z.string().nullable(),
});

export const esquemaAta = z.object({
  id: z.string().min(1),
  reuniao_id: z.string().min(1),
  estado: z.enum(["rascunho", "publicada"]),
  publicada: z.boolean(),
  resumo: z.string().nullable(),
  origem_do_resumo: esquemaRotulado,
  /* Nulo é o estado NORMAL — a ata escrita à mão funciona inteira e sozinha. E é nulo TAMBÉM
     para o cliente, sempre: mídia é do lado interno. */
  transcricao_midia_id: z.string().nullable(),
  publicado_em: z.string().nullable(),
  publicado_por_email: z.string().nullable(),
  criada_em: z.string().min(1),
  atualizada_em: z.string().min(1),
  itens: z.array(esquemaItemDaAta),
  /* Quantos itens este acesso NÃO enxerga. Sempre `0` para o cliente, pelo servidor: um contador
     de itens ocultos é a própria informação que a fronteira existe para não entregar. */
  ocultos: z.number(),
});

export const esquemaReuniaoDetalhada = esquemaReuniao.extend({
  ata: esquemaAta.nullable(),
});

const esquemaRespostaDasReunioes = z.object({
  implantacao_id: z.string().min(1),
  reunioes: z.array(esquemaReuniao),
  total: z.number(),
});

export const esquemaMidia = z.object({
  id: z.string().min(1),
  tipo: esquemaRotulado,
  origem: esquemaRotulado,
  estado: esquemaRotulado,
  derivada_de_id: z.string().nullable(),
  nome_original: z.string().nullable(),
  mime: z.string().nullable(),
  tamanho: z.number().nullable(),
  duracao_seg: z.number().nullable(),
  caracteres: z.number().nullable(),
  /* Por que o processamento não passou — "sem credencial do geo-ai-gateway" inclusive. Texto
     para gente ler, e é ele que a tela mostra em vez de um código de estado. */
  erro: z.string().nullable(),
  tentativas: z.number(),
  iniciado_em: z.string().nullable(),
  concluido_em: z.string().nullable(),
  criado_em: z.string().min(1),
  enviado_por: esquemaPessoa.nullable(),
});

/* O texto da transcrição sai só no detalhe de UM artefato, nunca na listagem: ele é a conversa
   inteira do cliente, e uma listagem que o carregasse o espalharia por todo log de proxy. */
export const esquemaMidiaDetalhada = esquemaMidia.extend({ texto: z.string().nullable() });

const esquemaEtapaDoPipeline = z.object({
  nome: z.string().min(1),
  disponivel: z.boolean(),
  motivo: z.string().nullable(),
});

const esquemaPipeline = z.object({
  disponivel: z.boolean(),
  etapas: z.array(esquemaEtapaDoPipeline),
  motivos: z.array(z.string()),
  caminho_manual: z.string().min(1),
});

const esquemaRespostaDasMidias = z.object({
  reuniao_id: z.string().min(1),
  midias: z.array(esquemaMidia),
  total: z.number(),
  pipeline: esquemaPipeline,
});

export const esquemaPendencia = z.object({
  id: z.string().min(1),
  ata_id: z.string().min(1),
  reuniao_id: z.string().min(1),
  reuniao_tipo: esquemaRotulado,
  reuniao_em: z.string().min(1),
  texto: z.string().min(1),
  responsavel: z.string().nullable(),
  responsavel_lado: z.enum(LADOS_DO_RESPONSAVEL).nullable(),
  prazo: z.string().nullable(),
  minuto: z.number().nullable(),
  visivel_ao_cliente: z.boolean(),
  jira_key: z.string().nullable(),
  jira_enviado_em: z.string().nullable(),
  jira_pendencia: z.string().nullable(),
  concluida_em: z.string().nullable(),
});

const esquemaRespostaDasPendencias = z.object({
  implantacao_id: z.string().min(1),
  pendencias: z.array(esquemaPendencia),
  total: z.number(),
  /* Quantas ainda não têm issue. É o número que a tela mostra como pendência do Jira desligado —
     nunca como erro. */
  sem_tarefa: z.number(),
  /* Quantas continuam abertas. `total` é o histórico; é este número que diz o tamanho da
     cobrança de hoje. */
  abertas: z.number(),
});

export type Reuniao = z.infer<typeof esquemaReuniao>;
export type ReuniaoDetalhada = z.infer<typeof esquemaReuniaoDetalhada>;
export type Ata = z.infer<typeof esquemaAta>;
export type ItemDaAta = z.infer<typeof esquemaItemDaAta>;
export type Midia = z.infer<typeof esquemaMidia>;
export type MidiaDetalhada = z.infer<typeof esquemaMidiaDetalhada>;
export type EstadoDoPipeline = z.infer<typeof esquemaPipeline>;
export type PendenciaDaAta = z.infer<typeof esquemaPendencia>;
export type ListaDeMidias = z.infer<typeof esquemaRespostaDasMidias>;
export type ListaDePendencias = z.infer<typeof esquemaRespostaDasPendencias>;

/* ------------------------------------------------------------------------------ caminhos */

export function caminhoDasReunioes(implantacaoId: string): string {
  return `/admin/implantacoes/${encodeURIComponent(implantacaoId)}/reunioes`;
}

export function caminhoDaReuniao(implantacaoId: string, reuniaoId: string): string {
  return `${caminhoDasReunioes(implantacaoId)}/${encodeURIComponent(reuniaoId)}`;
}

/** O endereço da ata na área do cliente. O painel aponta para cá — ver `ReunioesDoCliente`. */
export const CAMINHO_DAS_REUNIOES_DO_CLIENTE = "/app/reunioes";

/* ------------------------------------------------------------------------------ consultas */

export const CHAVE_RAIZ_DAS_REUNIOES = ["reunioes"] as const;

export function chaveDasReunioes(implantacaoId: string) {
  return ["reunioes", "implantacao", implantacaoId] as const;
}

export function chaveDaReuniao(reuniaoId: string) {
  return ["reunioes", "reuniao", reuniaoId] as const;
}

export function chaveDasMidias(reuniaoId: string) {
  return ["reunioes", "midias", reuniaoId] as const;
}

export function chaveDaMidia(midiaId: string) {
  return ["reunioes", "midia", midiaId] as const;
}

export function chaveDaAtaDaReuniao(reuniaoId: string) {
  return ["reunioes", "ata", reuniaoId] as const;
}

export function chaveDasPendencias(implantacaoId: string) {
  return ["reunioes", "pendencias", implantacaoId] as const;
}

function baseDaImplantacao(implantacaoId: string): string {
  return `/implantacoes/${encodeURIComponent(implantacaoId)}`;
}

function baseDaReuniao(reuniaoId: string): string {
  return `/reunioes/${encodeURIComponent(reuniaoId)}`;
}

function baseDaAta(ataId: string): string {
  return `/atas/${encodeURIComponent(ataId)}`;
}

export function useReunioes(implantacaoId: string) {
  return useQuery({
    queryKey: chaveDasReunioes(implantacaoId),
    queryFn: ({ signal }) =>
      pedirJson(`${baseDaImplantacao(implantacaoId)}/reunioes`, esquemaRespostaDasReunioes, {
        signal,
      }),
    enabled: implantacaoId.length > 0,
    retry: false,
  });
}

const esquemaPendenciasGeoTask = z.object({
  pendencias: z.array(z.string()),
  /* Operações que pararam no teto de tentativas: só voltam quando alguém pede no console. */
  abandonadas: z.number().int().nonnegative().default(0),
});

export function usePendenciasGeoTask(implantacaoId: string, habilitado: boolean) {
  return useQuery({
    queryKey: ["geotask", implantacaoId],
    queryFn: ({ signal }) => pedirJson(
      `${baseDaImplantacao(implantacaoId)}/geotask`, esquemaPendenciasGeoTask, { signal },
    ),
    enabled: habilitado && implantacaoId.length > 0,
    retry: false,
  });
}

/** Devolve ao lote as operações do GeoTask que pararam no teto, depois de corrigida a causa. */
export function useReativarGeoTask(implantacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => enviarJson(
      `${baseDaImplantacao(implantacaoId)}/geotask/reativar`,
      z.object({ reativadas: z.number().int() }),
      undefined,
    ),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ["geotask", implantacaoId] }),
  });
}

const esquemaTarefasTreinamento = z.object({
  tarefas: z.array(z.object({
    chave: z.string(), nome: z.string(), tipo: z.string(), status: z.string(),
  })),
  pendencia: z.string().nullable(),
  /* Escrita desligada (o padrão): publicar não move tarefa nem comenta, e a tela não promete. */
  escrita: z.boolean().default(false),
});

export function useTarefasTreinamento(ataId: string, habilitado: boolean) {
  return useQuery({
    queryKey: ["treinamento-geotask", ataId],
    queryFn: ({ signal }) => pedirJson(`${baseDaAta(ataId)}/geotask`, esquemaTarefasTreinamento, { signal }),
    enabled: habilitado && ataId.length > 0,
    retry: false,
  });
}

/**
 * Registra o uso do link sem colocar a sala atrás da disponibilidade da API.
 *
 * A navegação externa não deve esperar esta chamada: `keepalive` deixa o navegador terminar o
 * envio mesmo quando a pessoa troca de aba, e uma falha de telemetria nunca impede a entrada na
 * reunião. O servidor continua sendo a autoridade sobre sessão, visibilidade e auditoria.
 */
export function registrarEntradaNaReuniao(reuniaoId: string): void {
  void fetch(`${BASE_DA_API}/reunioes/${encodeURIComponent(reuniaoId)}/entrada`, {
    method: "POST",
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    keepalive: true,
  }).catch(() => undefined);
}

/** A reunião com a ata **já recortada pelo servidor** para quem está pedindo. */
export function useReuniao(reuniaoId: string) {
  return useQuery({
    queryKey: chaveDaReuniao(reuniaoId),
    queryFn: ({ signal }) =>
      pedirJson(baseDaReuniao(reuniaoId), esquemaReuniaoDetalhada, { signal }),
    enabled: reuniaoId.length > 0,
    retry: false,
  });
}

/**
 * A ata de UMA reunião, sozinha — o que o cartão do cliente pede sem carregar o resto.
 *
 * `404` é resposta esperada e vira `null`: para o cliente, reunião sem ata e reunião com ata em
 * rascunho respondem a mesma coisa, de propósito. Distinguir contaria que alguém escreveu algo
 * sobre a reunião dele que ele não pode ler.
 */
export function useAtaDaReuniao(reuniaoId: string, habilitada = true) {
  return useQuery({
    queryKey: chaveDaAtaDaReuniao(reuniaoId),
    queryFn: async ({ signal }) => {
      try {
        return await pedirJson(`${baseDaReuniao(reuniaoId)}/ata`, esquemaAta, { signal });
      } catch (causa) {
        if (causa instanceof ErroDaApi && causa.status === 404) return null;
        throw causa;
      }
    },
    enabled: habilitada && reuniaoId.length > 0,
    retry: false,
  });
}

export function useMidias(reuniaoId: string, habilitada = true) {
  return useQuery({
    queryKey: chaveDasMidias(reuniaoId),
    queryFn: ({ signal }) =>
      pedirJson(`${baseDaReuniao(reuniaoId)}/midias`, esquemaRespostaDasMidias, { signal }),
    enabled: habilitada && reuniaoId.length > 0,
    retry: false,
  });
}

/** O detalhe de um artefato — **o único lugar do portal com o texto da transcrição**. */
export function useMidia(midiaId: string) {
  return useQuery({
    queryKey: chaveDaMidia(midiaId),
    queryFn: ({ signal }) =>
      pedirJson(`/midias/${encodeURIComponent(midiaId)}`, esquemaMidiaDetalhada, { signal }),
    enabled: midiaId.length > 0,
    retry: false,
  });
}

export function usePendenciasDaAta(implantacaoId: string, habilitada = true) {
  return useQuery({
    queryKey: chaveDasPendencias(implantacaoId),
    queryFn: ({ signal }) =>
      pedirJson(`${baseDaImplantacao(implantacaoId)}/pendencias`, esquemaRespostaDasPendencias, {
        signal,
      }),
    enabled: habilitada && implantacaoId.length > 0,
    retry: false,
  });
}

/* ------------------------------------------------------------------------------- escritas */

/**
 * O que muda quando uma ata é publicada, e por isso o que precisa ser invalidado junto.
 *
 * Publicar não mexe só na ata: cada pendência do cliente marcada como visível vira
 * `PENDENCIA_ABERTA` na esteira dele, e a esteira alimenta o painel do cliente, a tela da
 * implantação no console e a carteira. Invalidar só a ata deixaria a tela ao lado dizendo, no
 * mesmo instante, que não há pendência nenhuma.
 */
async function invalidarTudoQueOlhaAEsteira(cliente: ReturnType<typeof useQueryClient>) {
  const chaves = [CHAVE_RAIZ_DAS_REUNIOES, CHAVE_RAIZ_DO_CONSOLE, CHAVE_MINHA_ESTEIRA, CHAVE_LISTAGEM];
  /* Cancela a busca em voo ANTES de invalidar. Uma consulta que ainda não tem dado (a primeira
     carga) não é recomeçada pela invalidação: ela reaproveita a requisição em curso, que pode ter
     lido o servidor antes deste ato — e a tela ficava sem a reunião recém-criada até atualizar a
     página. Mesmo cuidado de `recarregarSessao`. */
  await Promise.all(chaves.map((queryKey) => cliente.cancelQueries({ queryKey })));
  for (const queryKey of chaves) void cliente.invalidateQueries({ queryKey });
}

export interface PedidoDeReuniao {
  assunto: string;
  tipo: string;
  modulo_chave?: string | null;
  /** ISO **com fuso**. O servidor recusa horário sem ele — ver `paraInstanteComFuso`. */
  agendada_para: string;
  duracao_min: number;
  link: string | null;
  /** Sempre `manual` hoje. O campo existe para a tela não fingir que existe outro caminho. */
  modo: "manual";
  visivel_ao_cliente: boolean;
}

export function useAgendarReuniao(implantacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDeReuniao) =>
      enviarJson(`${baseDaImplantacao(implantacaoId)}/reunioes`, esquemaReuniao, corpo),
    onSuccess: () => {
      void invalidarTudoQueOlhaAEsteira(cliente);
      void cliente.invalidateQueries({ queryKey: ["geotask", implantacaoId] });
    },
  });
}

export interface PedidoDeRemarcacao {
  observacao: string;
  agendada_para?: string;
  duracao_min?: number;
  link?: string;
  /** `link: null` é ambíguo num corpo parcial; apagar o link é um ato com nome próprio. */
  limpar_link?: boolean;
}

export function useRemarcarReuniao(reuniaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDeRemarcacao) =>
      editarJson(baseDaReuniao(reuniaoId), esquemaReuniao, corpo),
    onSuccess: () => invalidarTudoQueOlhaAEsteira(cliente),
  });
}

/** Marca a reunião como realizada. Corpo vazio = agora, o caso de quem registra ao sair dela. */
export function useRegistrarRealizacao(reuniaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (realizadaEm: string | null) =>
      enviarJson(
        `${baseDaReuniao(reuniaoId)}/realizacao`,
        esquemaReuniao,
        realizadaEm ? { realizada_em: realizadaEm } : {},
      ),
    onSuccess: () => invalidarTudoQueOlhaAEsteira(cliente),
  });
}

/** Cancela com motivo — que é obrigatório porque é o que o cliente lê no lugar da reunião. */
export function useCancelarReuniao(reuniaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (motivo: string) =>
      enviarJson(`${baseDaReuniao(reuniaoId)}/cancelamento`, esquemaReuniao, { motivo }),
    onSuccess: () => invalidarTudoQueOlhaAEsteira(cliente),
  });
}

export interface EnvioDeMidia {
  tipo: "gravacao" | "audio" | "transcricao";
  arquivo: File;
  /** Duração da reunião em segundos, quando quem envia a conhece. */
  duracaoSeg?: number | null;
}

/**
 * Sobe um artefato — a gravação, o áudio extraído fora ou a transcrição pronta.
 *
 * Uma porta só, dividida por `tipo`, porque para quem usa o ato é um só: escolher o arquivo da
 * reunião. Ela **nasce concluída** nos três casos: upload não entra em fila, e por isso nenhum
 * upload pode ficar preso numa fila que ninguém drena.
 */
export function useEnviarMidia(reuniaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ tipo, arquivo, duracaoSeg }: EnvioDeMidia) => {
      const formulario = new FormData();
      formulario.append("tipo", tipo);
      formulario.append("arquivo", arquivo);
      if (duracaoSeg) formulario.append("duracao_seg", String(duracaoSeg));
      return enviarFormulario(`${baseDaReuniao(reuniaoId)}/midias`, esquemaMidia, formulario);
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaveDasMidias(reuniaoId) });
      void cliente.invalidateQueries({ queryKey: chaveDaReuniao(reuniaoId) });
    },
  });
}

/**
 * Transforma o texto colado num arquivo `.txt` e o envia pela mesma porta do upload.
 *
 * A API recebe transcrição por `multipart`, e não por JSON: é a mesma rota do arquivo de legenda
 * que o Meet e o Teams exportam. Colar é o mesmo ato com outro gesto — quem tem o texto na área
 * de transferência não deveria precisar salvá-lo em disco antes de o portal aceitá-lo.
 */
export function arquivoDeTranscricaoColada(texto: string, nome = "transcricao-colada.txt"): File {
  return new File([texto], nome, { type: "text/plain" });
}

export function useEnfileirarDerivacao(reuniaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ midiaId, tipo }: { midiaId: string; tipo: "audio" | "transcricao" }) =>
      enviarJson(
        `/midias/${encodeURIComponent(midiaId)}/derivacoes?tipo=${tipo}`,
        esquemaMidiaDetalhada,
        undefined,
      ),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaveDasMidias(reuniaoId) });
    },
  });
}

/**
 * Devolve à fila um artefato que falhou ou que ficou preso em `processando`.
 *
 * **Não é gateado pela disponibilidade do pipeline**, ao contrário de enfileirar: a linha já
 * existe, e recusar aqui a deixaria presa em `falhou` sem volta. Upload é que não se reprocessa —
 * os bytes já estão no disco, não há nada a refazer.
 */
export function useReprocessarMidia(reuniaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (midiaId: string) =>
      enviarJson(
        `/midias/${encodeURIComponent(midiaId)}/reprocessamento`,
        esquemaMidiaDetalhada,
        undefined,
      ),
    onSuccess: (midia) => {
      void cliente.invalidateQueries({ queryKey: chaveDasMidias(reuniaoId) });
      void cliente.invalidateQueries({ queryKey: chaveDaMidia(midia.id) });
    },
  });
}

export function useRemoverMidia(reuniaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (midiaId: string) => apagar(`/midias/${encodeURIComponent(midiaId)}`),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaveDasMidias(reuniaoId) });
      void cliente.invalidateQueries({ queryKey: chaveDaReuniao(reuniaoId) });
    },
  });
}

export interface PedidoDeAta {
  resumo?: string;
  transcricao_midia_id?: string;
  limpar_transcricao?: boolean;
}

/** Abre o rascunho. Sem mídia nenhuma é o caminho normal, não uma ata pela metade. */
export function useAbrirAta(reuniaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDeAta) =>
      enviarJson(`${baseDaReuniao(reuniaoId)}/ata`, esquemaAta, corpo),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DAS_REUNIOES });
    },
  });
}

/** Reescreve o resumo do rascunho. Ata publicada recusa com `409` — publicar é definitivo. */
export function useEditarAta(ataId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDeAta) => editarJson(baseDaAta(ataId), esquemaAta, corpo),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DAS_REUNIOES });
    },
  });
}

/**
 * **O ato.** Publicar é o que faz a ata existir para o cliente — antes disso ela não existe para
 * ele de forma alguma, e não é a consulta que esconde: é a policy da `0013`.
 */
export function usePublicarAta(ataId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (tarefasGeotask?: string[]) => enviarJson(
      `${baseDaAta(ataId)}/publicacao`, esquemaAta,
      tarefasGeotask ? { tarefas_geotask: tarefasGeotask } : undefined,
    ),
    onSuccess: () => invalidarTudoQueOlhaAEsteira(cliente),
  });
}

export interface PedidoDeItem {
  tipo: TipoDeItem;
  texto: string;
  responsavel?: string | null;
  responsavel_lado?: LadoDoResponsavel | null;
  prazo?: string | null;
  minuto?: number | null;
  visivel_ao_cliente: boolean;
}

export function useAcrescentarItem(ataId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDeItem) =>
      enviarJson(`${baseDaAta(ataId)}/itens`, esquemaItemDaAta, corpo),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DAS_REUNIOES });
    },
  });
}

/**
 * A correção de um item, **campo a campo**.
 *
 * Parcial de propósito, como o `PedidoDeEdicaoDeItem` do servidor: substituição inteira
 * obrigaria a tela a reenviar `visivel_ao_cliente` a cada correção de vírgula, e um campo
 * esquecido nesse reenvio mudaria em silêncio o lado para onde o item vai.
 */
export function useEditarItem(ataId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, corpo }: { itemId: string; corpo: Partial<PedidoDeItem> }) =>
      editarJson(
        `${baseDaAta(ataId)}/itens/${encodeURIComponent(itemId)}`,
        esquemaItemDaAta,
        corpo,
      ),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DAS_REUNIOES });
    },
  });
}

export function useRemoverItem(ataId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      apagar(`${baseDaAta(ataId)}/itens/${encodeURIComponent(itemId)}`),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DAS_REUNIOES });
    },
  });
}

/**
 * Liga a pendência à issue do Jira — ou registra por que ela ainda não tem uma.
 *
 * Sem `jira_key` o servidor **tenta abrir** e recusa com a pendência escrita: o portal não cria
 * issue no ONB (ADR 0002), e as credenciais estão vazias neste servidor de propósito. A resposta
 * é `200` nos dois casos, e a tela chama isso de pendência, nunca de erro.
 */
export function useVincularTarefa(ataId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, jiraKey }: { itemId: string; jiraKey: string | null }) =>
      enviarJson(
        `${baseDaAta(ataId)}/itens/${encodeURIComponent(itemId)}/tarefa`,
        esquemaItemDaAta,
        jiraKey ? { jira_key: jiraKey } : undefined,
      ),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DAS_REUNIOES });
    },
  });
}

/**
 * Dá a pendência por cumprida. **É o outro lado do "pendências viraram tarefa".**
 *
 * Vale em ata publicada de propósito, como o vínculo com o Jira: `concluida_em` é informação
 * sobre a tarefa, não sobre o que foi dito. Sem esta chamada a pendência ficava no cartão "o que
 * está com você" do cliente para sempre — o item congela ao publicar e nada a fechava.
 */
export function useConcluirPendencia() {
  const cliente = useQueryClient();
  return useMutation({
    /* A ata vem no parâmetro, e não no fechamento do hook: a lista de pendências cruza VÁRIAS
       atas da mesma implantação, e um hook por ata obrigaria a tela a criar um por linha. */
    mutationFn: ({ ataId, itemId }: { ataId: string; itemId: string }) =>
      enviarJson(
        `${baseDaAta(ataId)}/itens/${encodeURIComponent(itemId)}/conclusao`,
        esquemaItemDaAta,
        undefined,
      ),
    /* Mesma invalidação de `publicar`, e pela mesma razão: concluir emite `PENDENCIA_RESOLVIDA`
       na esteira, então o cartão "o que está com você" do cliente muda junto. */
    onSuccess: () => invalidarTudoQueOlhaAEsteira(cliente),
  });
}

/**
 * Endereço de download do arquivo de um artefato de mídia.
 *
 * Existe porque, sem ele, o upload de gravação era escrita sem leitura: nenhuma rota devolvia os
 * bytes e o único consumidor possível (a derivação) recusa enquanto o pipeline não estiver de pé.
 * O botão aceitava 100 MB de vídeo e ninguém, em papel nenhum, conseguia ouvir o arquivo depois.
 *
 * Mesma forma do anexo do kickoff, e pelo mesmo motivo: o servidor serve sempre como binário, e
 * a fronteira é a policy da `0013` mais `ata:ver_interno` — não o parâmetro da URL.
 */
export function urlDoArquivoDaMidia(midiaId: string): string {
  return `${BASE_DA_API}/midias/${encodeURIComponent(midiaId)}/arquivo`;
}

/* -------------------------------------------------------------- a fronteira, para a tela */

/** Para onde um item vai. Duas palavras, porque a tela precisa dizer isso sem depender de cor. */
export type DestinoDoItem = "cliente" | "interno";

/**
 * Para onde ESTE item vai — a **prévia**, e é só isso que ela é.
 *
 * Quem recorta de verdade é `visibilidade.py` e, antes dele, a policy `ata_item_isolamento` da
 * `0013`: para uma sessão de cliente uma nota interna **não existe**, em consulta nenhuma. Esta
 * função não protege coisa alguma — ela existe para quem escreve ver, enquanto escreve, se aquele
 * texto atravessa. O `tipo !== "nota_interna"` é redundante com o `CHECK`
 * `ck_ata_item_nota_interna_nunca_visivel`, e está aqui de propósito: numa fronteira, redundância
 * barata é o que transforma um erro de um lado só em erro de dois lados ao mesmo tempo.
 */
export function destinoDoItem(item: {
  tipo: TipoDeItem;
  visivel_ao_cliente: boolean;
}): DestinoDoItem {
  return item.visivel_ao_cliente && item.tipo !== "nota_interna" ? "cliente" : "interno";
}

export interface ContagemDaFronteira {
  vaoAoCliente: number;
  ficamDentro: number;
  notasInternas: number;
  pendenciasDoCliente: number;
  /** Pendência do cliente que NÃO atravessa — o servidor não a cobra dele, e nem poderia. */
  pendenciasDoClienteMudas: number;
}

/**
 * Quantos itens atravessam, quantos ficam, e quantas cobranças ficariam mudas.
 *
 * `pendenciasDoClienteMudas` é o número que evita o defeito mais silencioso da fatia: uma
 * pendência com responsável do lado do cliente que ninguém marcou como visível **não vira evento
 * na esteira dele** — o servidor não cobra alguém por um texto que ele não pode ler. Sem este
 * contador, quem publica acharia que cobrou.
 */
export function contarAFronteira(itens: readonly ItemDaAta[]): ContagemDaFronteira {
  let vaoAoCliente = 0;
  let notasInternas = 0;
  let pendenciasDoCliente = 0;
  let pendenciasDoClienteMudas = 0;

  for (const item of itens) {
    const paraOCliente = destinoDoItem(item) === "cliente";
    if (paraOCliente) vaoAoCliente += 1;
    if (item.tipo === "nota_interna") notasInternas += 1;
    if (item.tipo === "pendencia" && item.responsavel_lado === "cliente") {
      pendenciasDoCliente += 1;
      if (!paraOCliente) pendenciasDoClienteMudas += 1;
    }
  }

  return {
    vaoAoCliente,
    ficamDentro: itens.length - vaoAoCliente,
    notasInternas,
    pendenciasDoCliente,
    pendenciasDoClienteMudas,
  };
}

/* ------------------------------------------------------------------ leitura e formatação */

/* O fuso é fixo em São Paulo pelo mesmo motivo do resto do portal: o consultor e o cliente
   precisam ler a mesma hora, não a hora do navegador de cada um. */
const FUSO = "America/Sao_Paulo";

const FORMATO_DE_INSTANTE = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: FUSO,
});

const FORMATO_LONGO = new Intl.DateTimeFormat("pt-BR", {
  weekday: "long",
  day: "2-digit",
  month: "long",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: FUSO,
});

/** `2026-09-01T14:00:00-03:00` → `01/09/2026, 14:00`. */
export function formatarQuando(iso: string | null): string | null {
  if (!iso) return null;
  const momento = new Date(iso);
  return Number.isNaN(momento.getTime()) ? null : FORMATO_DE_INSTANTE.format(momento);
}

/** A mesma hora por extenso — o cabeçalho de uma reunião, onde cabe a frase inteira. */
export function formatarQuandoPorExtenso(iso: string): string | null {
  const momento = new Date(iso);
  return Number.isNaN(momento.getTime()) ? null : FORMATO_LONGO.format(momento);
}

const PARTES_LOCAIS = new Intl.DateTimeFormat("en-CA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: FUSO,
});

/**
 * ISO com fuso → o valor de um `<input type="datetime-local">`, **em horário de São Paulo**.
 *
 * O campo `datetime-local` não tem fuso: o navegador o interpreta no fuso da máquina. Deixar isso
 * solto faria a mesma reunião aparecer às 14:00 para quem marcou e às 17:00 para quem abriu o
 * portal de outro lugar — e as duas telas estariam "certas". O portal inteiro lê São Paulo, e o
 * campo também.
 */
export function paraCampoDeInstante(iso: string): string {
  const momento = new Date(iso);
  if (Number.isNaN(momento.getTime())) return "";
  const partes = Object.fromEntries(
    PARTES_LOCAIS.formatToParts(momento).map((parte) => [parte.type, parte.value]),
  );
  return `${partes.year}-${partes.month}-${partes.day}T${partes.hour}:${partes.minute}`;
}

const FORMATO_DE_DESLOCAMENTO = new Intl.DateTimeFormat("en-US", {
  timeZone: FUSO,
  timeZoneName: "longOffset",
});

/** `-03:00` para aquele instante. Calculado, e não fixado: horário de verão já existiu aqui. */
function deslocamentoDeSaoPaulo(momento: Date): string {
  const parte = FORMATO_DE_DESLOCAMENTO.formatToParts(momento).find(
    (item) => item.type === "timeZoneName",
  );
  const achado = /GMT([+-]\d{2}:\d{2})/.exec(parte?.value ?? "");
  // Sem `longOffset` no navegador, o Brasil está em -03:00 desde 2019 (fim do horário de verão).
  return achado?.[1] ?? "-03:00";
}

/**
 * O valor do `<input type="datetime-local">` → ISO **com fuso**, que é o que o servidor exige.
 *
 * A recusa do servidor é literal a respeito: *"sem fuso, o mesmo campo significa horas diferentes
 * para quem marcou e para quem vai entrar"*. Aqui o horário digitado é lido como horário de São
 * Paulo — o mesmo que a tela exibe — e sai carimbado com o deslocamento daquele dia.
 */
export function paraInstanteComFuso(valorLocal: string): string | null {
  if (!valorLocal) return null;
  // Primeiro palpite em UTC só para descobrir o deslocamento vigente naquela data; depois o
  // instante é remontado com o deslocamento certo. Sem os dois passos, uma data dentro de um
  // horário de verão passado sairia uma hora deslocada.
  const palpite = new Date(`${valorLocal}:00Z`);
  if (Number.isNaN(palpite.getTime())) return null;
  const deslocamento = deslocamentoDeSaoPaulo(palpite);
  const instante = new Date(`${valorLocal}:00${deslocamento}`);
  return Number.isNaN(instante.getTime()) ? null : instante.toISOString();
}

/** Agora, no formato do `<input type="datetime-local">` — o padrão de "marcar realizada". */
export function agoraNoCampoDeInstante(): string {
  return paraCampoDeInstante(new Date().toISOString());
}

/** `90` → `1h30`. Duração de reunião se lê em hora, não em minuto. */
export function formatarDuracao(minutos: number): string {
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (horas === 0) return `${resto}min`;
  return resto === 0 ? `${horas}h` : `${horas}h${String(resto).padStart(2, "0")}`;
}

/** O carimbo de minuto de um item da ata: `95` → `1:35` na gravação. */
export function formatarMinuto(minuto: number | null): string | null {
  if (minuto === null) return null;
  return `${Math.floor(minuto / 60)}:${String(minuto % 60).padStart(2, "0")}`;
}

export function formatarTamanho(bytes: number | null): string | null {
  if (bytes === null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** `2026-09-01` → `01/09/2026`, sem passar por `Date` — data pura não tem fuso. */
export function formatarPrazo(data: string | null): string | null {
  if (!data) return null;
  const partes = data.split("-");
  if (partes.length !== 3) return data;
  return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

export function rotuloDoTipoDeReuniao(tipo: string): string {
  return ROTULO_DO_TIPO_DE_REUNIAO[tipo] ?? tipo;
}

export function rotuloDoEstadoDaReuniao(estado: string): string {
  return ROTULO_DO_ESTADO_DA_REUNIAO[estado] ?? estado;
}

export function rotuloDoTipoDeMidia(tipo: string): string {
  return ROTULO_DO_TIPO_DE_MIDIA[tipo] ?? tipo;
}

export function rotuloDoEstadoDaMidia(estado: string): string {
  return ROTULO_DO_ESTADO_DA_MIDIA[estado] ?? estado;
}

export function rotuloDaEtapa(nome: string): string {
  return ROTULO_DA_ETAPA_DO_PIPELINE[nome] ?? nome;
}

/* Quanto tempo uma mídia pode ficar `processando` antes de ser considerada abandonada —
   `IDADE_MAXIMA_DE_PROCESSAMENTO` do worker, repetida aqui para a tela poder DIZER que aquela
   linha travou. "Processando para sempre" é o estado que ninguém enxerga; uma tela que o desenha
   igual a "processando há dez segundos" é como ele continua invisível. */
const MINUTOS_ATE_CONSIDERAR_TRAVADA = 30;

export function midiaTravada(midia: Midia): boolean {
  if (midia.estado !== "processando" || !midia.iniciado_em) return false;
  const iniciada = new Date(midia.iniciado_em).getTime();
  if (Number.isNaN(iniciada)) return false;
  return Date.now() - iniciada > MINUTOS_ATE_CONSIDERAR_TRAVADA * 60 * 1000;
}

/** Em que pé está a issue de uma pendência. O mesmo desenho do `estadoDoJira` do pacote. */
export type EstadoDaTarefa =
  | { estado: "vinculada"; chave: string; quando: string | null }
  | { estado: "pendente"; motivo: string }
  | { estado: "sem_registro" };

export function estadoDaTarefa(item: {
  jira_key: string | null;
  jira_enviado_em: string | null;
  jira_pendencia: string | null;
}): EstadoDaTarefa {
  if (item.jira_key) {
    return { estado: "vinculada", chave: item.jira_key, quando: item.jira_enviado_em };
  }
  if (item.jira_pendencia) return { estado: "pendente", motivo: item.jira_pendencia };
  /* Nem chave nem pendência: ninguém tentou. Dizer "pendente" aqui inventaria um diagnóstico que
     o servidor não escreveu. */
  return { estado: "sem_registro" };
}
